import React, { Fragment } from 'react';
import { object, arrayOf, shape, string, any, func, bool } from 'prop-types';
import moment from 'moment';
import {
  withStyles,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Grid from '@material-ui/core/Grid';
import { Translation, withTranslation } from 'react-i18next';
import LinearProgress from '@material-ui/core/LinearProgress';
import './divider.css';

const styles = theme => ({
  rootTop: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  rootBottom: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  quillContainer: {
    paddingBottom: theme.spacing(2),
  },
  leftButton: {
    display: 'flex',
  },
  rightButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    align: 'right',
    backgroundColor: '#357a38',
    color: '#ffffff',
  },
  saveButton: {
    background: '#0280CA',
  },
  divider: {
    borderTop: '1px dashed #8c8b8b',
    backgroundColor: 'transparent',
    marginTop: 10,
    marginBottom: 10,
  },
  sectionDivider: {
    marginTop: theme.spacing(4),
  },
  message: {
    margin: 10,
  },
  success: {
    backgroundColor: '#357a38',
    flexGrow: 1,
  },
  grow: {
    flexGrow: 1,
    color: 'white',
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#6fbf73',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',
      contrastText: '#ffffff',
    },
  },
  typography: {
    useNextVariants: true,
  },
});

const Message = ({ message, divider, classes }) => (
  <Fragment>
    {divider && <Divider classes={{ root: classes.divider }} />}
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography variant="subtitle1">
        {message.source === 'OUT' ? 'Visa Officer' : 'You'}
      </Typography>
      <Typography variant="caption">
        {moment(message.createdAt).format('DD/MM/YYYY, H:mm a')}
      </Typography>
    </Grid>
    <Typography variant="body1" className={classes.message}>
      {message.message}
    </Typography>
    {(message.documents || []).map(doc => (
      <Typography
        key={doc.uuid}
        variant="caption"
        gutterBottom
      >{`<< ${doc.filename} >>`}</Typography>
    ))}
  </Fragment>
);

class AwaitingFeedBack extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      message: '',
      text: '',
      file: {},
    };
    this.latestFileEventTarget = null;
    this.quillElement = React.createRef();
  }

  componentDidMount() {
    const { t } = this.props;
    this.quillElement.current.getEditor().root.dataset.placeholder = t(
      'startWritingResponse'
    );
  }

  componentDidUpdate() {
    const { t } = this.props;
    this.quillElement.current.getEditor().root.dataset.placeholder = t(
      'startWritingResponse'
    );
  }

  componentWillUnmount() {
    this.latestFileEventTarget = null;
  }

  pickDocument = e => {
    const files = e.target.files;
    this.latestFileEventTarget = e.target;
    this.setState({ file: files[0] });
  };

  handleChange = (content, delta, source, editor) => {
    this.setState({ text: content, message: editor.getText() });
  };

  onSend = () => {
    if (this.state.file.name) {
      this.props.send(this.state.message, this.state.file, '999');
    } else {
      this.props.send(this.state.message, this.props.history);
    }
    if (this.latestFileEventTarget !== null) {
      this.latestFileEventTarget.value = null;
      this.latestFileEventTarget = null;
    }
    this.setState({ text: '', message: '', file: {} });
  };

  toggleDocumentPreview = doc => {
    this.setState({ doc });
  };

  render() {
    const {
      match,
      isSent,
      classes,
      email,
      messages = [],
      isFetching,
    } = this.props;
    const { to } = match.params;
    const messageToOfficer = to === 'officer';
    const { message, file = {} } = this.state;
    const filename = file.name || '';
    return (
      <Translation>
        {t => {
          return (
            <Grid justifyContent="center" direction="row" container>
              {messageToOfficer && (
                <Grid item xs={11}>
                  <Paper className={classes.rootTop} elevation={3}>
                    {messages.map((message, index) => (
                      <Message
                        key={message.uuid}
                        message={message}
                        divider={index > 0}
                        classes={classes}
                      />
                    ))}
                  </Paper>
                </Grid>
              )}
              <Grid item xs={11}>
                <Paper className={classes.rootBottom} elevation={3}>
                  {isFetching && (
                    <LinearProgress className={classes.progress} />
                  )}
                  {!isFetching && isSent && (
                    <div className={classes.success}>
                      <Typography
                        variant="body1"
                        color="inherit"
                        className={classes.grow}
                      >
                        {`${t('emailSentTo')} ${email}`}
                      </Typography>
                    </div>
                  )}
                  <ReactQuill
                    ref={this.quillElement}
                    className={classes.quillContainer}
                    value={this.state.text}
                    onChange={this.handleChange}
                  />
                  {messageToOfficer && (
                    <input
                      type="file"
                      name="file"
                      onChange={this.pickDocument}
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={11}>
                <Grid container direction="row" justifyContent="flex-end">
                  <Grid item xs={6} className={classes.leftButton}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.saveButton}
                      disabled={isFetching}
                      component={Link}
                      to={'/applications'}
                    >
                      {t('backTo')}
                    </Button>
                  </Grid>
                  <Grid item xs={6} className={classes.rightButton}>
                    <MuiThemeProvider theme={theme}>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={this.onSend}
                        disabled={
                          isFetching ||
                          (message.length === 0 && filename.length === 0)
                        }
                      >
                        {t('send')}
                      </Button>
                    </MuiThemeProvider>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          );
        }}
      </Translation>
    );
  }
}

AwaitingFeedBack.propTypes = {
  classes: object.isRequired,
  messages: arrayOf(
    shape({
      message: string.isRequired,
      uuid: any.isRequired,
      createdAt: string.isRequired,
    })
  ).isRequired,
  send: func.isRequired,
  isFetching: bool.isRequired,
  error: string,
  upload: func.isRequired,
};

export default withTranslation()(withStyles(styles)(AwaitingFeedBack));



// WEBPACK FOOTER //
// ./src/view/forms/MessageToAgent/AwaitingFeedback.js